import { useMutation } from '@tanstack/react-query';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { EntityType } from '../../../../../data-models/field2.data-model';
import { Rubric } from '../../../../../schemas/Rubric.schema';
import {
  createOrUpdateRubric,
  deleteRubricByEntity,
  getRubricsForConfigId,
  ICreateOrUpdateRubricPayload,
} from '../../../../../services/queries/MaggieRubricQueries';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { invalidateMostRecentRubricsByCompanyIdAtom } from '../../../../../services/state/RubricsStateJ';
import { LoadingId } from '../../../../../types';

export function useCreateOrEditRubric() {
  const { pushErrorToast } = useToastMessageState();
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();

  return useMutation({
    mutationFn: async (payload: ICreateOrUpdateRubricPayload) => {
      startLoading(LoadingId.createOrEditRubric);
      return await createOrUpdateRubric(payload);
    },
    onMutate: (payload: ICreateOrUpdateRubricPayload) => {
      return { payload };
    },
    onSuccess: async (data, variables: ICreateOrUpdateRubricPayload) => {
      await invalidateMostRecentRubricsByCompanyIdAtom(variables.entityId);
    },
    onError: async (err) => {
      console.error(err);
      pushErrorToast({ message: getErrorMessage(err, 'Failed to create/update rubric') });
    },
    onSettled() {
      stopLoading(LoadingId.createOrEditRubric);
    },
    retry: false,
  });
}

export function useDeleteRubric() {
  const { pushErrorToast } = useToastMessageState();
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();

  return useMutation({
    mutationFn: async (rubric: Rubric) => {
      startLoading(LoadingId.deleteRubric);
      const { entity, entityId, rubricConfigId } = rubric;
      const history = await getRubricsForConfigId({
        entity: entity as EntityType,
        entityId,
        rubricConfigId,
      });
      await Promise.all(history.map((h) => deleteRubricByEntity(h)));
    },
    onSuccess: async (data, variables: Rubric) => {
      await invalidateMostRecentRubricsByCompanyIdAtom(variables.entityId);
    },
    onError: async (err) => {
      console.error(err);
      pushErrorToast({ message: getErrorMessage(err, 'Failed to delete rubric') });
    },
    onSettled() {
      stopLoading(LoadingId.deleteRubric);
    },
    retry: false,
  });
}
